// ═══════════════════════════════════════════════════════════════
// BULK UPDATE MODAL
// Path: src/components/features/BulkUpdateModal.jsx
//
// 3-step wizard: Find → Preview → Update
// Admin only. Operates on donor records in bulk.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Search,
  Eye,
  Zap,
  X,
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  AlertTriangle,
} from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { ActionButton } from "../ui/ActionButton";
import { supabase } from "../../lib/supabase";
import { debugLog } from "../../utils/debugLog";
import { contactLogService } from "../../services/contactLogService";

const PORTFOLIO_MOVES = [
  { id: "cultivate", label: "Cultivate" },
  { id: "propose", label: "Propose" },
  { id: "acknowledge", label: "Acknowledge" },
  { id: "thank", label: "Thank" },
  { id: "report", label: "Report" },
  { id: "renew", label: "Renew" },
];

export const BulkUpdateModal = ({ onClose, onComplete }) => {
  const { palette, members, pathways, directors, activeFunds, showMessage } = useApp();

  const [step, setStep] = useState(1);

  // ── Gift data (loaded from Supabase) ─────────────────────────
  const [giftCounts, setGiftCounts] = useState(null); // { memberId: count }
  const [giftRows, setGiftRows] = useState(null);     // raw rows for date-filtered totals
  const [loadingGifts, setLoadingGifts] = useState(false);

  const loadGiftCounts = useCallback(async () => {
    setLoadingGifts(true);
    try {
      // Fetch gift rows per member (cursor-paginated by id)
      let allRows = [];
      let lastId = null;
      const pageSize = 1000;
      while (true) {
        let query = supabase
          .from("gifts")
          .select("id, member_id, amount, gift_date, fund_id")
          .order("id", { ascending: true })
          .limit(pageSize);

        if (lastId) query = query.gt("id", lastId);

        const { data, error } = await query;
        if (error) throw error;
        if (!data || data.length === 0) break;
        allRows = allRows.concat(data);
        lastId = data[data.length - 1].id;
        if (data.length < pageSize) break;
      }

      const counts = {};
      allRows.forEach((row) => {
        counts[row.member_id] = (counts[row.member_id] || 0) + 1;
      });
      setGiftCounts(counts);
      setGiftRows(allRows);
    } catch (err) {
      showMessage("Failed to load gift counts", "error");
    } finally {
      setLoadingGifts(false);
    }
  }, [showMessage]);

  // Load gift counts on mount
  useEffect(() => {
    loadGiftCounts();
  }, [loadGiftCounts]);

  // ── Step 1: Criteria ──────────────────────────────────────
  const [filters, setFilters] = useState({
    giftCountOp: "",       // "", "eq", "lt", "gt", "lte", "gte"
    giftCountVal: "",
    giftTotalOp: "",       // "", "eq", "lt", "gt", "lte", "gte"
    giftTotalVal: "",
    giftTotalFrom: "",     // YYYY-MM-DD date range for total giving
    giftTotalTo: "",
    giftFundId: "",        // "" = all funds, uuid = specific fund
    pathwayIs: "",
    pathwayIsNot: "",
    isDonor: "",           // "", "true", "false"
    isBoardMember: "",     // "", "true", "false"
    directorIs: "",
    directorUnassigned: false,
    contactDaysOp: "",     // "", "gt", "lt"
    contactDaysVal: "",
  });

  // ── Step 3: Action ────────────────────────────────────────
  const [action, setAction] = useState({
    type: "pathway",       // "pathway", "isDonor", "isBoardMember", "director"
    value: "",
  });
  const [executing, setExecuting] = useState(false);
  const [results, setResults] = useState(null);

  // ── Compute matching donors ───────────────────────────────
  const matched = useMemo(() => {
    let result = [...members];

    // Pre-filter gift rows by fund if selected
    const fundFilteredRows = giftRows
      ? filters.giftFundId
        ? giftRows.filter((r) => r.fund_id === filters.giftFundId)
        : giftRows
      : null;

    // Fund-only filter: if fund is selected but no count/total op, show donors with any gift to that fund
    if (filters.giftFundId && !filters.giftCountOp && !filters.giftTotalOp && fundFilteredRows) {
      const fundMemberIds = new Set(fundFilteredRows.map((r) => String(r.member_id)));
      result = result.filter((m) => fundMemberIds.has(String(m.id)));
    }

    // Gift count filter (uses data from Supabase, respects fund filter)
    if (filters.giftCountOp && filters.giftCountVal !== "" && fundFilteredRows) {
      const target = parseInt(filters.giftCountVal, 10);
      const counts = {};
      fundFilteredRows.forEach((row) => {
        counts[row.member_id] = (counts[row.member_id] || 0) + 1;
      });
      result = result.filter((m) => {
        const count = counts[m.id] || 0;
        switch (filters.giftCountOp) {
          case "eq": return count === target;
          case "lt": return count < target;
          case "gt": return count > target;
          case "lte": return count <= target;
          case "gte": return count >= target;
          default: return true;
        }
      });
    }

    // Gift total filter (computes date-filtered totals from raw rows, respects fund filter)
    if (filters.giftTotalOp && filters.giftTotalVal !== "" && fundFilteredRows) {
      const target = parseFloat(filters.giftTotalVal);
      const fromDate = filters.giftTotalFrom || null;
      const toDate = filters.giftTotalTo || null;

      // Build per-member totals filtered by date range
      const filteredTotals = {};
      fundFilteredRows.forEach((row) => {
        const giftDate = row.gift_date || "";
        if (fromDate && giftDate < fromDate) return;
        if (toDate && giftDate > toDate) return;
        filteredTotals[row.member_id] = (filteredTotals[row.member_id] || 0) + (parseFloat(row.amount) || 0);
      });

      result = result.filter((m) => {
        const total = filteredTotals[m.id] || 0;
        switch (filters.giftTotalOp) {
          case "eq": return total === target;
          case "lt": return total < target;
          case "gt": return total > target;
          case "lte": return total <= target;
          case "gte": return total >= target;
          default: return true;
        }
      });
    }

    // Pathway filter
    if (filters.pathwayIs) {
      result = result.filter((m) => m.pathway === filters.pathwayIs);
    }
    if (filters.pathwayIsNot) {
      result = result.filter((m) => m.pathway !== filters.pathwayIsNot);
    }

    // Donor flag
    if (filters.isDonor === "true") {
      result = result.filter((m) => m.isDonor);
    } else if (filters.isDonor === "false") {
      result = result.filter((m) => !m.isDonor);
    }

    // Board flag
    if (filters.isBoardMember === "true") {
      result = result.filter((m) => m.isBoardMember);
    } else if (filters.isBoardMember === "false") {
      result = result.filter((m) => !m.isBoardMember);
    }

    // Director
    if (filters.directorUnassigned) {
      result = result.filter((m) => !m.assignedDirector?.trim());
    } else if (filters.directorIs) {
      result = result.filter((m) => m.assignedDirector === filters.directorIs);
    }

    // Contact days
    if (filters.contactDaysOp && filters.contactDaysVal) {
      const days = parseInt(filters.contactDaysVal, 10);
      const now = new Date();
      result = result.filter((m) => {
        const logs = m.contactLog || [];
        if (logs.length === 0) {
          return filters.contactDaysOp === "gt"; // no contact = infinite days
        }
        const latest = logs.reduce((max, log) => {
          const d = new Date(log.logDate || log.createdAt);
          return d > max ? d : max;
        }, new Date(0));
        const diffDays = Math.floor((now - latest) / (1000 * 60 * 60 * 24));
        if (filters.contactDaysOp === "gt") return diffDays > days;
        if (filters.contactDaysOp === "lt") return diffDays < days;
        return true;
      });
    }

    return result;
  }, [members, filters, giftCounts, giftRows]);

  // ── Execute bulk update ───────────────────────────────────
  const handleExecute = async () => {
    if (matched.length === 0) return;
    setExecuting(true);

    try {
      const ids = matched.map((m) => m.id);

      // Build snake_case payload matching actual DB column names
      let dbPayload = {};
      switch (action.type) {
        case "pathway":
          dbPayload = { pathway: action.value };
          break;
        case "isDonor":
          dbPayload = { is_donor: action.value === "true" };
          break;
        case "isBoardMember":
          dbPayload = { is_board_member: action.value === "true" };
          break;
        case "director":
          dbPayload = { assigned_director: action.value || "" };
          break;
        case "donorType":
          dbPayload = { donor_type: action.value };
          break;
        case "portfolioMove":
          dbPayload = {
            portfolio_move: action.value,
            portfolio_move_date: new Date().toISOString(),
          };
          break;
        default:
          throw new Error("Unknown action type");
      }

      // Simple direct update — no .select(), no join
      let updated = 0;
      let errors = 0;

      const moveLabel = action.type === "portfolioMove"
        ? PORTFOLIO_MOVES.find((m) => m.id === action.value)?.label || action.value
        : null;

      for (const id of ids) {
        const { error } = await supabase
          .from("members")
          .update(dbPayload)
          .eq("id", id);

        if (error) {
          debugLog.error("BulkUpdate row", { id, error: error.message });
          errors += 1;
        } else {
          updated += 1;
          // Auto-create contact log for portfolio move transitions
          if (action.type === "portfolioMove") {
            const member = matched.find((m) => m.id === id);
            const fromLabel = PORTFOLIO_MOVES.find((m) => m.id === (member?.portfolioMove || "cultivate"))?.label || "Cultivate";
            try {
              await contactLogService.create(id, {
                type: "portfolio_move",
                subject: `${fromLabel} → ${moveLabel}`,
                notes: `Bulk portfolio move: ${fromLabel} → ${moveLabel}`,
                date: new Date().toISOString(),
              });
            } catch (logErr) {
              debugLog.error("BulkUpdate contact log", { id, error: logErr.message });
            }
          }
        }
      }

      setResults({ updated, errors, total: ids.length });
      setStep(4);
      showMessage(`Bulk update: ${updated} donors updated`);
    } catch (err) {
      showMessage("Bulk update failed: " + err.message, "error");
    } finally {
      setExecuting(false);
    }
  };

  // ── Styles ────────────────────────────────────────────────
  const label = {
    fontSize: "0.65rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: palette.text3,
    fontFamily: FONT_FAMILY,
    display: "block",
    marginBottom: 3,
  };

  const sel = {
    padding: "6px 10px",
    borderRadius: 6,
    fontSize: FONT_SIZES.xs,
    fontFamily: FONT_FAMILY,
    border: `1px solid ${palette.inputBorder}`,
    background: palette.inputBg,
    color: palette.inputColor,
    cursor: "pointer",
    width: "100%",
  };

  const inp = {
    ...sel,
    cursor: "text",
    width: 80,
  };

  // ── Render ────────────────────────────────────────────────
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        style={{
          background: palette.cardBg,
          borderRadius: 14,
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
          width: "100%",
          maxWidth: 700,
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "14px 20px",
            borderBottom: `1px solid ${palette.cardBorder}`,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: FONT_SIZES.md,
              fontWeight: 700,
              color: palette.text1,
              fontFamily: FONT_FAMILY,
            }}
          >
            <Zap size={ICON_SIZES.md} style={{ color: palette.accent1 }} />
            Bulk Update
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                display: "flex",
                gap: 4,
                fontSize: "0.6rem",
                fontWeight: 600,
                color: palette.text3,
                fontFamily: FONT_FAMILY,
              }}
            >
              {["Find", "Preview", "Update", "Done"].map((s, idx) => (
                <span
                  key={s}
                  style={{
                    padding: "2px 8px",
                    borderRadius: 10,
                    background:
                      step === idx + 1 ? palette.accent1 : `${palette.text3}15`,
                    color: step === idx + 1 ? "#fff" : palette.text3,
                  }}
                >
                  {s}
                </span>
              ))}
            </div>
            <button
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: palette.text3,
                padding: 4,
              }}
            >
              <X size={ICON_SIZES.md} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
          {/* ── STEP 1: Find ─────────────────────────────── */}
          {step === 1 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <p
                style={{
                  fontSize: FONT_SIZES.sm,
                  color: palette.text2,
                  fontFamily: FONT_FAMILY,
                  margin: 0,
                }}
              >
                Define criteria to find donors. All conditions are combined
                (AND). Leave a filter empty to skip it.
              </p>

              {/* Fund filter */}
              <div>
                <span style={label}>Gave to fund</span>
                <select
                  value={filters.giftFundId}
                  onChange={(e) =>
                    setFilters((p) => ({ ...p, giftFundId: e.target.value }))
                  }
                  style={sel}
                >
                  <option value="">— Any Fund —</option>
                  {(activeFunds || []).map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Gift count */}
              <div>
                <span style={label}>Number of gifts {filters.giftFundId ? "(in selected fund)" : ""}</span>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <select
                    value={filters.giftCountOp}
                    onChange={(e) =>
                      setFilters((p) => ({ ...p, giftCountOp: e.target.value }))
                    }
                    style={{ ...sel, width: 160 }}
                  >
                    <option value="">— Skip —</option>
                    <option value="eq">Exactly</option>
                    <option value="lt">Less than</option>
                    <option value="lte">At most</option>
                    <option value="gt">More than</option>
                    <option value="gte">At least</option>
                  </select>
                  {filters.giftCountOp && (
                    <input
                      type="number"
                      min="0"
                      value={filters.giftCountVal}
                      onChange={(e) =>
                        setFilters((p) => ({ ...p, giftCountVal: e.target.value }))
                      }
                      placeholder="1"
                      style={inp}
                    />
                  )}
                  {loadingGifts && (
                    <span style={{ fontSize: "0.6rem", color: palette.text3, fontStyle: "italic" }}>
                      Loading gift data...
                    </span>
                  )}
                </div>
              </div>

              {/* Total giving amount */}
              <div>
                <span style={label}>Total giving amount ($) {filters.giftFundId ? "(in selected fund)" : ""}</span>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <select
                    value={filters.giftTotalOp}
                    onChange={(e) =>
                      setFilters((p) => ({ ...p, giftTotalOp: e.target.value }))
                    }
                    style={{ ...sel, width: 160 }}
                  >
                    <option value="">— Skip —</option>
                    <option value="eq">Exactly</option>
                    <option value="lt">Less than</option>
                    <option value="lte">At most</option>
                    <option value="gt">More than</option>
                    <option value="gte">At least</option>
                  </select>
                  {filters.giftTotalOp && (
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <span style={{ fontSize: FONT_SIZES.sm, color: palette.text2, fontFamily: FONT_FAMILY }}>$</span>
                      <input
                        type="number"
                        min="0"
                        step="any"
                        value={filters.giftTotalVal}
                        onChange={(e) =>
                          setFilters((p) => ({ ...p, giftTotalVal: e.target.value }))
                        }
                        placeholder="1000"
                        style={{ ...inp, width: 120 }}
                      />
                    </div>
                  )}
                  {loadingGifts && !giftRows && (
                    <span style={{ fontSize: "0.6rem", color: palette.text3, fontStyle: "italic" }}>
                      Loading...
                    </span>
                  )}
                </div>
                {filters.giftTotalOp && (
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6 }}>
                    <span style={{ fontSize: FONT_SIZES.xs, color: palette.text3, fontFamily: FONT_FAMILY }}>
                      Between
                    </span>
                    <input
                      type="date"
                      value={filters.giftTotalFrom}
                      onChange={(e) =>
                        setFilters((p) => ({ ...p, giftTotalFrom: e.target.value }))
                      }
                      style={{ ...inp, width: 140 }}
                    />
                    <span style={{ fontSize: FONT_SIZES.xs, color: palette.text3, fontFamily: FONT_FAMILY }}>
                      and
                    </span>
                    <input
                      type="date"
                      value={filters.giftTotalTo}
                      onChange={(e) =>
                        setFilters((p) => ({ ...p, giftTotalTo: e.target.value }))
                      }
                      style={{ ...inp, width: 140 }}
                    />
                    {(!filters.giftTotalFrom && !filters.giftTotalTo) && (
                      <span style={{ fontSize: "0.6rem", color: palette.text3, fontFamily: FONT_FAMILY, fontStyle: "italic" }}>
                        Leave blank for all time
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Pathway is */}
              <div>
                <span style={label}>Pathway is</span>
                <select
                  value={filters.pathwayIs}
                  onChange={(e) =>
                    setFilters((p) => ({ ...p, pathwayIs: e.target.value }))
                  }
                  style={sel}
                >
                  <option value="">— Any —</option>
                  {pathways.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Pathway is NOT */}
              <div>
                <span style={label}>Pathway is NOT</span>
                <select
                  value={filters.pathwayIsNot}
                  onChange={(e) =>
                    setFilters((p) => ({ ...p, pathwayIsNot: e.target.value }))
                  }
                  style={sel}
                >
                  <option value="">— Any —</option>
                  {pathways.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Donor flag */}
              <div>
                <span style={label}>Donor flag</span>
                <select
                  value={filters.isDonor}
                  onChange={(e) =>
                    setFilters((p) => ({ ...p, isDonor: e.target.value }))
                  }
                  style={sel}
                >
                  <option value="">— Any —</option>
                  <option value="true">Is a donor</option>
                  <option value="false">Is NOT a donor</option>
                </select>
              </div>

              {/* Board flag */}
              <div>
                <span style={label}>Board flag</span>
                <select
                  value={filters.isBoardMember}
                  onChange={(e) =>
                    setFilters((p) => ({
                      ...p,
                      isBoardMember: e.target.value,
                    }))
                  }
                  style={sel}
                >
                  <option value="">— Any —</option>
                  <option value="true">Is a board member</option>
                  <option value="false">Is NOT a board member</option>
                </select>
              </div>

              {/* Director */}
              <div>
                <span style={label}>Assigned Director</span>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <select
                    value={
                      filters.directorUnassigned ? "__unassigned" : filters.directorIs
                    }
                    onChange={(e) => {
                      if (e.target.value === "__unassigned") {
                        setFilters((p) => ({
                          ...p,
                          directorUnassigned: true,
                          directorIs: "",
                        }));
                      } else {
                        setFilters((p) => ({
                          ...p,
                          directorUnassigned: false,
                          directorIs: e.target.value,
                        }));
                      }
                    }}
                    style={sel}
                  >
                    <option value="">— Any —</option>
                    <option value="__unassigned">Unassigned</option>
                    {(directors || []).map((d) => (
                      <option key={d.id} value={d.name}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Days since contact */}
              <div>
                <span style={label}>Days since last contact</span>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <select
                    value={filters.contactDaysOp}
                    onChange={(e) =>
                      setFilters((p) => ({
                        ...p,
                        contactDaysOp: e.target.value,
                      }))
                    }
                    style={{ ...sel, width: 160 }}
                  >
                    <option value="">— Skip —</option>
                    <option value="gt">More than</option>
                    <option value="lt">Less than</option>
                  </select>
                  {filters.contactDaysOp && (
                    <>
                      <input
                        type="number"
                        min="0"
                        value={filters.contactDaysVal}
                        onChange={(e) =>
                          setFilters((p) => ({
                            ...p,
                            contactDaysVal: e.target.value,
                          }))
                        }
                        placeholder="30"
                        style={inp}
                      />
                      <span
                        style={{
                          fontSize: FONT_SIZES.xs,
                          color: palette.text2,
                          fontFamily: FONT_FAMILY,
                        }}
                      >
                        days
                      </span>
                    </>
                  )}
                </div>
              </div>

              {/* Live count */}
              <div
                style={{
                  padding: "10px 14px",
                  borderRadius: 8,
                  background: `${palette.accent1}08`,
                  border: `1px solid ${palette.cardBorder}`,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    fontSize: FONT_SIZES.xs,
                    color: palette.text2,
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  Matching donors:
                </span>
                <span
                  style={{
                    fontSize: FONT_SIZES.lg,
                    fontWeight: 700,
                    color: matched.length > 0 ? palette.accent1 : palette.text3,
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  {matched.length.toLocaleString()}
                </span>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <ActionButton
                  label="Preview"
                  icon={ArrowRight}
                  onClick={() => setStep(2)}
                  disabled={matched.length === 0}
                />
              </div>
            </div>
          )}

          {/* ── STEP 2: Preview ──────────────────────────── */}
          {step === 2 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div
                style={{
                  textAlign: "center",
                  padding: "12px 0",
                  fontSize: FONT_SIZES.xl,
                  fontWeight: 700,
                  color: palette.accent1,
                  fontFamily: FONT_FAMILY,
                }}
              >
                {matched.length.toLocaleString()} donors matched
              </div>

              {/* Sample list */}
              <div
                style={{
                  border: `1px solid ${palette.cardBorder}`,
                  borderRadius: 8,
                  overflow: "hidden",
                  maxHeight: 250,
                  overflowY: "auto",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: FONT_SIZES.xs,
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        background: `${palette.accent1}08`,
                        position: "sticky",
                        top: 0,
                      }}
                    >
                      <th
                        style={{
                          padding: "6px 10px",
                          textAlign: "left",
                          fontWeight: 700,
                          color: palette.text3,
                          borderBottom: `1px solid ${palette.cardBorder}`,
                        }}
                      >
                        Name
                      </th>
                      <th
                        style={{
                          padding: "6px 10px",
                          textAlign: "left",
                          fontWeight: 700,
                          color: palette.text3,
                          borderBottom: `1px solid ${palette.cardBorder}`,
                        }}
                      >
                        Pathway
                      </th>
                      <th
                        style={{
                          padding: "6px 10px",
                          textAlign: "left",
                          fontWeight: 700,
                          color: palette.text3,
                          borderBottom: `1px solid ${palette.cardBorder}`,
                        }}
                      >
                        Director
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {matched.slice(0, 50).map((m) => {
                      const pw = pathways.find((p) => p.id === m.pathway);
                      return (
                        <tr key={m.id}>
                          <td
                            style={{
                              padding: "5px 10px",
                              borderBottom: `1px solid ${palette.cardBorder}`,
                              fontWeight: 600,
                              color: palette.text1,
                            }}
                          >
                            {m.lastName}, {m.firstName}
                          </td>
                          <td
                            style={{
                              padding: "5px 10px",
                              borderBottom: `1px solid ${palette.cardBorder}`,
                              color: pw?.color || palette.text2,
                            }}
                          >
                            {pw?.name || m.pathway || "—"}
                          </td>
                          <td
                            style={{
                              padding: "5px 10px",
                              borderBottom: `1px solid ${palette.cardBorder}`,
                              color: palette.text2,
                            }}
                          >
                            {m.assignedDirector || "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {matched.length > 50 && (
                  <div
                    style={{
                      padding: "8px 10px",
                      textAlign: "center",
                      fontSize: FONT_SIZES.xs,
                      color: palette.text3,
                      fontFamily: FONT_FAMILY,
                      fontStyle: "italic",
                    }}
                  >
                    Showing first 50 of {matched.length.toLocaleString()}
                  </div>
                )}
              </div>

              <div
                style={{ display: "flex", justifyContent: "space-between" }}
              >
                <ActionButton
                  label="Back"
                  icon={ArrowLeft}
                  variant="secondary"
                  onClick={() => setStep(1)}
                />
                <ActionButton
                  label="Choose Action"
                  icon={ArrowRight}
                  onClick={() => setStep(3)}
                />
              </div>
            </div>
          )}

          {/* ── STEP 3: Update ───────────────────────────── */}
          {step === 3 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "10px 14px",
                  background: "#fef3c7",
                  border: "1px solid #fde68a",
                  borderRadius: 8,
                  fontSize: FONT_SIZES.xs,
                  color: "#92400e",
                  fontFamily: FONT_FAMILY,
                }}
              >
                <AlertTriangle size={ICON_SIZES.sm} />
                This will update{" "}
                <strong>{matched.length.toLocaleString()} donors</strong>.
                Review carefully before executing.
              </div>

              {/* Action type */}
              <div>
                <span style={label}>What to change</span>
                <select
                  value={action.type}
                  onChange={(e) =>
                    setAction({ type: e.target.value, value: "" })
                  }
                  style={sel}
                >
                  <option value="pathway">Change Pathway</option>
                  <option value="portfolioMove">Set Portfolio Move</option>
                  <option value="isDonor">Set Donor Flag</option>
                  <option value="isBoardMember">Set Board Flag</option>
                  <option value="director">Assign Director</option>
                  <option value="donorType">Set Donor Type</option>
                </select>
              </div>

              {/* Action value */}
              <div>
                <span style={label}>New value</span>
                {action.type === "pathway" && (
                  <select
                    value={action.value}
                    onChange={(e) =>
                      setAction((p) => ({ ...p, value: e.target.value }))
                    }
                    style={sel}
                  >
                    <option value="">— Select pathway —</option>
                    {pathways.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                )}
                {(action.type === "isDonor" ||
                  action.type === "isBoardMember") && (
                  <select
                    value={action.value}
                    onChange={(e) =>
                      setAction((p) => ({ ...p, value: e.target.value }))
                    }
                    style={sel}
                  >
                    <option value="">— Select —</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                )}
                {action.type === "director" && (
                  <select
                    value={action.value}
                    onChange={(e) =>
                      setAction((p) => ({ ...p, value: e.target.value }))
                    }
                    style={sel}
                  >
                    <option value="">Unassigned</option>
                    {(directors || []).map((d) => (
                      <option key={d.id} value={d.name}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                )}
                {action.type === "donorType" && (
                  <select
                    value={action.value}
                    onChange={(e) =>
                      setAction((p) => ({ ...p, value: e.target.value }))
                    }
                    style={sel}
                  >
                    <option value="">— Select —</option>
                    <option value="individual">Individual</option>
                    <option value="household">Household</option>
                    <option value="organization">Organization</option>
                  </select>
                )}
                {action.type === "portfolioMove" && (
                  <select
                    value={action.value}
                    onChange={(e) =>
                      setAction((p) => ({ ...p, value: e.target.value }))
                    }
                    style={sel}
                  >
                    <option value="">— Select move —</option>
                    {PORTFOLIO_MOVES.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Summary */}
              {action.value && (
                <div
                  style={{
                    padding: "12px 16px",
                    background: `${palette.accent1}08`,
                    borderRadius: 8,
                    border: `1px solid ${palette.cardBorder}`,
                    fontSize: FONT_SIZES.sm,
                    fontFamily: FONT_FAMILY,
                    color: palette.text1,
                    textAlign: "center",
                  }}
                >
                  Set{" "}
                  <strong>
                    {action.type === "pathway"
                      ? "pathway"
                      : action.type === "portfolioMove"
                        ? "portfolio move"
                        : action.type === "isDonor"
                          ? "donor flag"
                          : action.type === "isBoardMember"
                            ? "board flag"
                            : action.type === "donorType"
                              ? "donor type"
                              : "director"}
                  </strong>{" "}
                  to{" "}
                  <strong>
                    {action.type === "pathway"
                      ? pathways.find((p) => p.id === action.value)?.name
                      : action.type === "portfolioMove"
                        ? PORTFOLIO_MOVES.find((m) => m.id === action.value)?.label
                        : action.type === "director"
                          ? action.value || "Unassigned"
                          : action.type === "donorType"
                            ? action.value
                            : action.value === "true"
                              ? "Yes"
                              : "No"}
                  </strong>{" "}
                  for <strong>{matched.length.toLocaleString()}</strong> donors
                </div>
              )}

              <div
                style={{ display: "flex", justifyContent: "space-between" }}
              >
                <ActionButton
                  label="Back"
                  icon={ArrowLeft}
                  variant="secondary"
                  onClick={() => setStep(2)}
                />
                <ActionButton
                  label={executing ? "Updating..." : "Execute Update"}
                  icon={Zap}
                  onClick={handleExecute}
                  disabled={!action.value || executing}
                />
              </div>
            </div>
          )}

          {/* ── STEP 4: Done ─────────────────────────────── */}
          {step === 4 && results && (
            <div
              style={{
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                gap: 16,
                alignItems: "center",
                padding: "20px 0",
              }}
            >
              <CheckCircle size={ICON_SIZES.xxl} style={{ color: "#16a34a" }} />
              <div
                style={{
                  fontSize: FONT_SIZES.lg,
                  fontWeight: 700,
                  color: palette.text1,
                  fontFamily: FONT_FAMILY,
                }}
              >
                Update Complete
              </div>
              <div
                style={{
                  fontSize: FONT_SIZES.sm,
                  color: palette.text2,
                  fontFamily: FONT_FAMILY,
                }}
              >
                <strong>{results.updated.toLocaleString()}</strong> donors
                updated
                {results.errors > 0 && (
                  <span style={{ color: "#dc2626" }}>
                    {" "}· {results.errors} errors
                  </span>
                )}
              </div>
              <ActionButton
                label="Done"
                onClick={() => {
                  onComplete?.();
                  onClose();
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
